import React from 'react'
import {
  formatToLocalDate,
  toLocaleNonAmericanDateString,
  formatShortLocalDateStringToDate,
  getWeekNumber,
  getDateOfISOWeek,
} from './../../../shared/libraries/utils/date'
import { compareStringSimilarity } from './../../../Utils/Filters'
import { downloadStringAsFile } from './../../../Utils/Misc'
import { isDollarsAdjustment } from './../../../Utils/Validators/csv'
import { authorization } from '../../../Module/Auth/Auth'
import {
  getCampaignsLink,
  getCampaignLink,
  getCityPlanLink,
} from '../../../Utils/Link'
import { PATHS } from '../../../Config/Routing/paths'
import CampaignsIcon from '../../../Assets/Images/icons/campaigns'
import CitiesIcon from '../../../Assets/Images/icons/cities'
import { groupMissionsByWeek } from '../../../Utils/Mission'
import {
  CAMPAIGN_ENUM_TYPES,
  LOCATION_STATUS_ENUM,
} from '../../../shared/constants'

/**
 *
 * @param {Object} data
 */
const objectToUpper = data => {
  const uppers = {}

  for (const [key, value] of Object.entries(data)) {
    uppers[key.toUpperCase()] = value
    if (key.toUpperCase() === 'PAYMENT_ADJUSTMENT') {
      uppers['PAY_ADJUSTMENT'] = value
    }
  }

  return uppers
}

export const convertCreateMissionDtoToMission = (d, { locations }) => {
  var locationsHash = {}
  d = objectToUpper(d)
  locations.forEach(l => {
    locationsHash[l.name.toLowerCase()] = l.id
    locationsHash[String(l.id)] = l.id
  })

  const locationId =
    d['LOCATION'] && d['LOCATION'].toLowerCase
      ? d['LOCATION'].toLowerCase()
      : d['LOCATION']

  // eslint-disable-next-line no-console
  console.log({
    locationsHash,
    value: d['LOCATION'],
    locationId,
    locations,
  })
  var dateTime = getDateFromStrings(d['DATE'], d['START_TIME'])

  return {
    locationId: locationsHash[locationId],
    locationName: d['LOCATION'],
    dateTime: formatToLocalDate(dateTime),
    draftPayAdjustment: parseFloat(d['PAY_ADJUSTMENT']),
  }
}

/**
 * Get a Date object from the csv provided date and time strings.
 * @param {string} dateStr
 * @param {string} timeStr
 * @returns {Date}
 */
export const getDateFromStrings = function(dateStr, timeStr) {
  var dateTime
  if (/\d{4}-\d{2}-\d{2}/.test(dateStr)) dateTime = new Date(dateStr)
  else dateTime = formatShortLocalDateStringToDate(dateStr)
  // TODO Validate the format of the fields
  var [hours, minutes] = timeStr.split(':')
  dateTime.setHours(Number(hours))
  dateTime.setMinutes(Number(minutes))
  return dateTime
}

/**
 * Find the closest matching string in an array of candidates
 * @param {string} str
 * @param {Array<string>} candidates
 * @return {string}
 */
const findSimilarString = function(str, candidates) {
  let max = 0
  return candidates.reduce((agg, candidate) => {
    const delta = compareStringSimilarity(candidate, str)
    if (delta > max) {
      max = delta
      return candidate
    }

    return agg
  }, '')
}

export const maybeGetSuggestionText = function(str, candidates) {
  var suggestionStr = findSimilarString(str, candidates)
  if (suggestionStr) return `Did you mean '${suggestionStr}'?`
  return
}

/**
 * Confirms that the raw spreadsheet data is in the correct format. And there is
 * the required data to create a new mission. Will throw on the first
 * encountered error.
 * @param {object} d An object representing the csv imported row
 */
export const validateCreateMissionDto = (
  d,
  { locations, dateRange, now, campaignType },
) => {
  // Allow YYYY-MM-DD or DD/MM/YYYY or D/M/YYYY or DD/MM/YY
  if (
    !/\d{4}-\d{2}-\d{2}/.test(d['DATE']) &&
    !/\d{1,2}\/\d{1,2}\/\d{2,4}/.test(d['DATE'])
  )
    throw new Error('Date does not match expected format DD/MM/YYYY.')

  if (!/^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/.test(d['START_TIME']))
    throw new Error(
      `START_TIME: ${
        d['START_TIME']
      } is not valid. It has to be in 24 hour format, such as 08:30 or 19:00.`,
    )

  if (!d['LOCATION']) throw new Error('Missing location.')

  if (
    d['PAY_ADJUSTMENT'] &&
    d['PAY_ADJUSTMENT'].length &&
    !isDollarsAdjustment(d['PAY_ADJUSTMENT'])
  )
    throw new Error(
      'Payout is not valid, expects a dollar amount with cents as two decimal places. e.g. 57.00',
    )

  let locationsHash = {}
  const locationNames = []

  locations.forEach(l => {
    locationsHash[l.name.toLowerCase()] = l.id
    locationsHash[String(l.id)] = l.id
    locationNames.push(l.name.toLowerCase())
  })
  const locationId =
    d['LOCATION'] && d['LOCATION'].toLowerCase
      ? d['LOCATION'].toLowerCase()
      : d['LOCATION']

  // eslint-disable-next-line no-console
  console.log({
    locationsHash,
    value: d['LOCATION'],
    locationId,
    locations,
  })
  if (!campaignType || CAMPAIGN_ENUM_TYPES.DIRECTMAIL !== campaignType) {
    if (!locationsHash[locationId]) {
      var error = new Error(`Location '${d['LOCATION']}' not found.`)
      error.suggestion = maybeGetSuggestionText(d['LOCATION'], locationNames)
      throw error
    }
  }

  if (now) {
    var dateTime = getDateFromStrings(d['DATE'], d['START_TIME'])
    if (dateTime < now) throw new Error(`Mission cannot be in the past.`)
  }

  // Validate roster date range
  if (dateRange) {
    var [startDate, endDate] = dateRange
    var dateTime = getDateFromStrings(d['DATE'], d['START_TIME'])
    if (dateTime < startDate || dateTime > endDate)
      throw new Error(
        `Mission must start after 
        ${toLocaleNonAmericanDateString(startDate)} and before
        ${toLocaleNonAmericanDateString(endDate)}.`.replace(/\s+/g, ' '),
      )
  }
}

export const downloadSampleCSV = function({ locations, dateRange }) {
  var exampleWNum = getWeekNumber((dateRange && dateRange[0]) || new Date())
  var exampleDate = dateRange
    ? toLocaleNonAmericanDateString(dateRange[0])
    : '13/06/2019'
  // Grab a random location
  var exampleLocation
  if (locations.length) {
    var exampleIndex = Math.floor(Math.random() * locations.length)
    exampleLocation = locations[exampleIndex].name
  } else {
    exampleLocation = 'Central Station'
  }

  const examplePayAdjustment = ''

  downloadStringAsFile(
    `DATE,START_TIME,LOCATION,PAY_ADJUSTMENT
${exampleDate},08:00,${exampleLocation},${examplePayAdjustment}`,
    `roster-w${exampleWNum}.csv`,
  )
}

/**
 * Calculates whether user can import missions to the current roster
 * @param {number} param.week Roster's week
 * @param {number} param.year Roster's year
 *
 * @returns {boolean} Indicates whether user can import missions to the roster
 */

export const canImport = ({ week, year }) => {
  if (!authorization.isCityManager()) return false
  const startDate = getDateOfISOWeek(week, year)
  const endDate = new Date(startDate)
  endDate.setDate(endDate.getDate() + 7)
  if (endDate < new Date()) return false
  return true
}

export const getRosterTitle = week => `Roster - Week ${week}`

const filterCityPlanLocationByCampaign = ({ locations, campaign }) =>
  locations.reduce((acc, { node }) => {
    if (node.status === LOCATION_STATUS_ENUM.ENABLED) {
      if (!campaign.isSelfServe && !node.isSelfServe) {
        acc.push(node)
      }

      if (campaign.isSelfServe) {
        if (!node.campaignId || node.campaignId === campaign.id) {
          acc.push(node)
        }
      }
    }

    return acc
  }, [])

/**
 * (mapStateToProps) Agreggator function that formats data for RosterView component using redux state and component's props
 * @param {object} state Redux State
 * @param {object} props RosterView Props
 *
 * @returns {object} Data for component comsumption
 */
export const formatDataForComponentConsumption = ({
  campaign,
  cityPlan,
  rosterId,
}) => {
  const { missions, city } = cityPlan
  const groupedRosters = groupMissionsByWeek(missions)
  const rosterMissions = groupedRosters[rosterId] || []
  const cityLocations = filterCityPlanLocationByCampaign({
    locations: city.locations.edges,
    campaign,
  })

  return {
    cityLocations,
    campaign,
    cityPlan,
    city,
    missions: rosterMissions,
    countryCode: city.country.code,
  }
}

/**
 * Generates breadcrumbs for the RosterView page that can be consumed by <BreadcrumbsRenderer />
 *
 * @param {object} param.campaign Roster's campaign data
 * @param {number} param.cityPlanId Roster's city plan ID
 * @param {number} param.campaignId Roster's campaign ID
 * @param {boolean} param.fromCityScreen Indicates whether user come to this page from the city screen
 * @param {object} param.city Roster's city
 *
 * @returns {array} Items that can be consumed by <BreadcrumbsRenderer />
 */
export const getBreadcrumbs = ({
  campaign,
  cityPlanId,
  campaignId,
  fromCityScreen,
  city,
}) => {
  const campaignsPathname = getCampaignsLink()
  const campaignPathname = getCampaignLink(campaignId)
  const cityPlanPathname = getCityPlanLink(campaignId, cityPlanId)

  if (fromCityScreen) {
    return {
      icon: <CitiesIcon style={{ width: 24 }} />,
      items: [
        {
          label: 'Cities',
          link: PATHS.main.dashboard + PATHS.dashboard.cities,
        },
        {
          label: city.name,
          link:
            PATHS.main.dashboard +
            PATHS.dashboard.cities +
            `/${city.id}/roster`,
        },
        {
          label: 'View a Roster',
        },
      ],
    }
  }

  return {
    icon: <CampaignsIcon style={{ width: 16 }} />,
    items: [
      {
        label: 'Campaigns',
        link: `${campaignsPathname}?Status=Active`,
      },
      {
        label: campaign.name,
        link: campaignPathname,
      },
      {
        label: `${city.name} City Plan`,
        link: cityPlanPathname,
      },
      {
        label: 'View a Roster',
      },
    ],
  }
}
